/**
 * Derive a club's lifecycle status from its data signals, for the /admin club
 * list. The raw list is flat and noisy — many clubs are bare placeholders with
 * no racing. Grouping by derived status lets the noise collapse.
 *
 *   'active'   — running racing: has at least one season (event).
 *   'early'    — interest registered but no seasons yet (a prospect).
 *   'inactive' — no seasons and no interest, or explicitly archived. Noise.
 *
 * Derivation order (first match wins):
 *   1. Explicitly archived (club.status === 'archived')  → inactive
 *   2. Has ≥1 season                                     → active
 *   3. Has interest (registrations or interest_count)    → early
 *   4. Otherwise                                          → inactive
 */
export const CLUB_STATUS = {
  ACTIVE: 'active',
  EARLY: 'early',
  INACTIVE: 'inactive',
};

/** Display metadata per status, in the order groups should render. */
export const STATUS_ORDER = [CLUB_STATUS.ACTIVE, CLUB_STATUS.EARLY, CLUB_STATUS.INACTIVE];

export const STATUS_META = {
  [CLUB_STATUS.ACTIVE]: { label: 'Active', hint: 'Running racing — seasons in play' },
  [CLUB_STATUS.EARLY]: { label: 'Early interest', hint: 'Interest registered, no seasons yet' },
  [CLUB_STATUS.INACTIVE]: { label: 'Inactive', hint: 'No seasons, no interest, or archived' },
};

/** Best available interest count for a club: max of aggregated registrations
 *  and the denormalized interest_count on the club doc (which may be stale). */
export function clubInterestCount(club, interestByClub = {}) {
  const fromRegs = interestByClub[club.id] || 0;
  const fromDoc = club.interest_count || 0;
  return Math.max(fromRegs, fromDoc);
}

/** Compact, data-derived signals for a single club.
 *  `seasons` is the club's events array (from loadAllClubs seasonMap). */
export function summarizeClub(club, seasons = [], interestByClub = {}) {
  const list = Array.isArray(seasons) ? seasons : [];
  let classes = 0;
  let documents = 0;
  let published = 0;
  let seasonInterest = 0;
  for (const s of list) {
    classes += Array.isArray(s.classes) ? s.classes.length : 0;
    documents += Array.isArray(s.documents) ? s.documents.length : 0;
    if ((s.content_version || 0) > 0) published += 1;
    seasonInterest += s.interest_count || 0;
  }
  const interest = Math.max(clubInterestCount(club, interestByClub), seasonInterest);
  return {
    seasonCount: list.length,
    classes,
    documents,
    published,
    interest,
  };
}

/** Derive the lifecycle status for a club from its data. */
export function deriveClubStatus(club, seasons = [], interestByClub = {}) {
  if (club?.status === 'archived') return CLUB_STATUS.INACTIVE;
  const list = Array.isArray(seasons) ? seasons : [];
  if (list.length > 0) return CLUB_STATUS.ACTIVE;
  if (clubInterestCount(club, interestByClub) > 0) return CLUB_STATUS.EARLY;
  return CLUB_STATUS.INACTIVE;
}

/**
 * Group clubs by derived status. Returns
 *   { active: Entry[], early: Entry[], inactive: Entry[] }
 * where Entry = { club, seasons, status, summary }. Input order is preserved
 * within each group (loadAllClubs already sorts clubs by name).
 */
export function groupClubsByStatus(clubs = [], seasonMap = {}, interestByClub = {}) {
  const groups = {
    [CLUB_STATUS.ACTIVE]: [],
    [CLUB_STATUS.EARLY]: [],
    [CLUB_STATUS.INACTIVE]: [],
  };
  for (const club of clubs) {
    const seasons = seasonMap[club.id] || [];
    const status = deriveClubStatus(club, seasons, interestByClub);
    const summary = summarizeClub(club, seasons, interestByClub);
    groups[status].push({ club, seasons, status, summary });
  }
  return groups;
}

/** Aggregate interest registrations into a { [clubId]: count } map.
 *  Mirrors the club-level tally in interestStore.recalculateInterestCounts. */
export function aggregateInterestByClub(registrations = []) {
  const counts = {};
  for (const r of registrations) {
    if (r.club_id && r.club_id !== 'other') {
      counts[r.club_id] = (counts[r.club_id] || 0) + 1;
    }
  }
  return counts;
}
